import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../services/api';
import { ArrowLeft, Download, FileText, BarChart3, TrendingUp, Plus } from 'lucide-react';

interface Report {
  id: string;
  title: string;
  type: 'simulation' | 'machine' | 'analysis';
  createdAt: string;
  status: 'ready' | 'generating' | 'error';
}

export default function Reports() {
  const navigate = useNavigate();
  const [reports, setReports] = useState<Report[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchReports = async () => {
      try {
        const mockReports: Report[] = [
          { id: '1', title: 'Simulação de Financiamento — Máquina XYZ', type: 'simulation', createdAt: new Date().toISOString(), status: 'ready' },
          { id: '2', title: 'Análise de Máquinas — Renovação', type: 'machine', createdAt: new Date(Date.now() - 86400000).toISOString(), status: 'ready' },
          { id: '3', title: 'Análise de Viabilidade — Projeto 2024', type: 'analysis', createdAt: new Date(Date.now() - 172800000).toISOString(), status: 'ready' },
        ];
        setReports(mockReports);
      } catch (error) {
        console.error('Failed to fetch reports:', error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchReports();
  }, []);

  const getTypeConfig = (type: string) => {
    switch (type) {
      case 'simulation': return { icon: BarChart3, color: '#2563eb', bg: '#eff6ff', label: 'Simulação' };
      case 'machine': return { icon: TrendingUp, color: '#059669', bg: '#f0fdf4', label: 'Máquina' };
      case 'analysis': return { icon: FileText, color: '#7c3aed', bg: '#f5f3ff', label: 'Análise' };
      default: return { icon: FileText, color: '#6b7280', bg: '#f9fafb', label: 'Relatório' };
    }
  };

  const formatDate = (dateString: string) => new Intl.DateTimeFormat('pt-BR', {
    year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit',
  }).format(new Date(dateString));

  if (isLoading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f8fafc', fontFamily: 'system-ui, sans-serif' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ width: 40, height: 40, border: '4px solid #e0e0e0', borderTopColor: '#059669', borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto' }} />
          <p style={{ marginTop: 12, color: '#6b7280', fontSize: 14 }}>Carregando relatórios...</p>
        </div>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: '#f8fafc', fontFamily: 'system-ui, -apple-system, sans-serif' }}>
      {/* Header */}
      <header style={{ background: '#fff', borderBottom: '1px solid #e5e7eb', position: 'sticky', top: 0, zIndex: 50 }}>
        <div style={{ maxWidth: 900, margin: '0 auto', padding: '0 24px', height: 60, display: 'flex', alignItems: 'center', gap: 16 }}>
          <button onClick={() => navigate('/dashboard')} style={{ background: 'none', border: '1px solid #e5e7eb', borderRadius: 8, padding: '6px 12px', fontSize: 13, color: '#6b7280', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
            <ArrowLeft size={14} /> Voltar
          </button>
          <div>
            <h1 style={{ fontSize: 20, fontWeight: 800, color: '#111827', margin: 0 }}>Meus Relatórios</h1>
            <p style={{ fontSize: 12, color: '#6b7280', margin: 0 }}>Histórico de documentos gerados</p>
          </div>
        </div>
      </header>

      <main style={{ maxWidth: 900, margin: '0 auto', padding: '28px 24px' }}>
        {reports.length === 0 ? (
          <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 20, padding: '60px 24px', textAlign: 'center' }}>
            <FileText size={48} color="#d1d5db" style={{ margin: '0 auto 16px' }} />
            <h3 style={{ fontSize: 18, fontWeight: 700, color: '#111827', margin: '0 0 8px' }}>Nenhum relatório ainda</h3>
            <p style={{ fontSize: 14, color: '#6b7280', margin: '0 0 24px' }}>Comece criando uma simulação</p>
            <button onClick={() => navigate('/simulator')} style={{ background: '#059669', color: '#fff', border: 'none', borderRadius: 10, padding: '11px 24px', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
              Criar Simulação
            </button>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {reports.map((report) => {
              const config = getTypeConfig(report.type);
              const Icon = config.icon;
              return (
                <div key={report.id} style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 16, padding: '20px 24px', display: 'flex', alignItems: 'center', gap: 16, boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
                  <div style={{ width: 48, height: 48, borderRadius: 14, background: config.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <Icon size={22} color={config.color} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <h3 style={{ fontSize: 16, fontWeight: 700, color: '#111827', margin: '0 0 6px' }}>{report.title}</h3>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                      <span style={{ background: config.bg, color: config.color, fontSize: 12, fontWeight: 600, padding: '3px 10px', borderRadius: 999 }}>{config.label}</span>
                      <span style={{ fontSize: 13, color: '#6b7280' }}>{formatDate(report.createdAt)}</span>
                      {report.status === 'ready' && (
                        <span style={{ background: '#f0fdf4', color: '#16a34a', fontSize: 12, fontWeight: 600, padding: '3px 10px', borderRadius: 999 }}>✓ Pronto</span>
                      )}
                    </div>
                  </div>
                  <button onClick={() => alert(`Download de "${report.title}" em desenvolvimento`)}
                    style={{ background: '#059669', color: '#fff', border: 'none', borderRadius: 10, padding: '9px 18px', fontSize: 13, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                    <Download size={14} /> Download
                  </button>
                </div>
              );
            })}
          </div>
        )}

        {/* Gerar novo */}
        <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 20, padding: '24px', marginTop: 24, boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
          <h3 style={{ fontSize: 17, fontWeight: 700, color: '#111827', margin: '0 0 16px', display: 'flex', alignItems: 'center', gap: 8 }}>
            <Plus size={18} color="#059669" /> Gerar Novo Relatório
          </h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
            {[
              { icon: BarChart3, label: 'Simulação', desc: 'Calcular financiamento', color: '#2563eb', bg: '#eff6ff', path: '/simulator' },
              { icon: TrendingUp, label: 'Máquinas', desc: 'Análise de equipamentos', color: '#059669', bg: '#f0fdf4', path: '/dashboard' },
              { icon: FileText, label: 'Análise', desc: 'Viabilidade de projeto', color: '#7c3aed', bg: '#f5f3ff', path: '/dashboard' },
            ].map((btn, i) => {
              const Icon = btn.icon;
              return (
                <button key={i} onClick={() => navigate(btn.path)}
                  style={{ background: btn.bg, border: `1px solid ${btn.color}30`, borderRadius: 12, padding: '16px', cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, transition: 'all 0.15s' }}
                  onMouseEnter={e => e.currentTarget.style.transform = 'translateY(-2px)'}
                  onMouseLeave={e => e.currentTarget.style.transform = 'none'}
                >
                  <Icon size={22} color={btn.color} />
                  <span style={{ fontSize: 14, fontWeight: 700, color: btn.color }}>{btn.label}</span>
                  <span style={{ fontSize: 12, color: '#6b7280' }}>{btn.desc}</span>
                </button>
              );
            })}
          </div>
        </div>
      </main>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}